<template>
  <div class="flex w-full flex-col items-center">
    <form
      class="z-20 flex w-full grow flex-col text-black focus:outline-none"
      @submit.prevent="onSubmit"
    >
      <div class="relative mb-4">
        <div class="mb-4">
          <UiTextInput
            ref="loginInputRef"
            v-model="model.login"
            v-ui-test-name="'loginEmail'"
            type="email"
            autocomplete="email"
            label="Электронная почта"
            placeholder="Электронная почта"
            maxlength="50"
            :invalid="!!errors.login.length"
            :errors="errorMessages.login"
          />
        </div>
        <div class="mb-2">
          <UiTextInput
            v-model="model.plainPassword"
            v-ui-test-name="'loginPassword'"
            type="password"
            autocomplete="current-password"
            is-password-toggle
            class="mb-2"
            label="Пароль"
            placeholder="Пароль"
            maxlength="30"
            :invalid="!!errors.login.length"
            :errors="errorMessages.plainPassword"
          />
          <div v-if="errors.login.length">
            <div
              v-for="(error, key) in errors.login"
              :key="key"
              v-ui-test-name="'authenticationAttemptFormError'"
              class="mt-1 flex text-xs text-error-500 first:-mt-1"
            >
              {{ error.message }}
            </div>
          </div>
        </div>
        <div
          :class="{
            'sm:absolute sm:bottom-0.5 sm:right-0':
              errorMessages.plainPassword.length,
          }"
          class="pr-4 text-right text-xs"
        >
          <UiButton
            v-ui-test-name="'forgetPassword'"
            underline
            class="text-neutral-600 !no-underline hover:!underline"
            @click="openRecoveryForm"
          >
            Забыли пароль?
          </UiButton>
        </div>
      </div>
      <UiButton
        v-ui-test-name="'loginButton'"
        :loading="loading"
        type="submit"
        primary
        :class="{ 'max-w-[13.75rem] md:max-w-none': maxSizeButton }"
      >
        Войти
      </UiButton>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { useVuelidate } from '@vuelidate/core'

import { email, required, helpers } from '@vuelidate/validators'
import { AuthenticationAttempt } from '@/models'

defineProps({
  maxSizeButton: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'login'): void
}>()

const { login } = useAuthStore()
const { currentUser, errors } = storeToRefs(useAuthStore())
const { closeModal } = useModalStore()
const { currentLocation } = useLocationStore()
const { createAnalyticsEvent } = useAnalyticsStore()

const { loading } = useLoading()
const { withMessage } = helpers

const model = reactive(new AuthenticationAttempt())
const rules = {
  login: {
    required: withMessage('Введите электронную почту', required),
    email: withMessage('Электронная почта введена неверно', email),
  },
  plainPassword: {
    required: withMessage('Введите пароль', required),
  },
}
const loginInputRef = ref<any>(null)
const v$ = useVuelidate(rules, model)
const errorMessages = computed(() =>
  useErrorMessages<Partial<AuthenticationAttempt>>(v$, rules),
)

model.domain = currentLocation?.domain || ''

const onSubmit = async () => {
  loading.value = true
  v$.value.$touch()
  if (v$.value.$invalid) return

  await login(model)

  loading.value = false

  if (errors.value.login.length) return

  closeModal('authenticationAttemptFormModal')

  createAnalyticsEvent({
    event: 'login',
    value: {
      user_id: currentUser.value!.id,
    },
  })

  createAnalyticsEvent({
    event: 'authorization_success',
    value: {
      placement: 'Личный кабинет-всплывающее окно',
    },
    isRequiredValuesNeed: true,
    yandexMetrika: true,
  })

  emit('login')

  loading.value = false
}

onMounted(() => {
  loginInputRef.value?.$el.querySelector('input').focus()
})

onUnmounted(() => {
  errors.value.login = []
})

const { showModal } = useModalStore()
const recoveryCard = defineAsyncComponent(
  () =>
    import('~/components/entities/RecoveryPassword/RecoveryPasswordCard.vue'),
)

const openRecoveryForm = () => {
  closeModal('authenticationAttemptFormModal')

  showModal({
    key: 'recoveryModal',
    modalConfig: {
      component: recoveryCard,
      isModalCloseButtonShown: true,
      modalSize: useDevice().isMobile ? 'bottom' : 'medium',
      showOverlay: true,
    },
  })
}
</script>
